import List from 'components/product/list';
import Main from '../../layouts/main';
import { useSelector } from 'react-redux';
import Dashboard from '../../layouts/dashboard';
import Head from 'next/head';
import React, { useMemo } from 'react';
import useTranslation from '../../hooks/useTranslation';
import useH1Title from '../../hooks/useH1Title';
import { useRouterWithLang } from 'hooks/useRouterWithLang';
import { getCookieFromServer } from '../../tools/cookie';

export default function Page() {
  const isLoggedIn = useSelector((s) => s.isLoggedIn);
  const { TRANSLATIONS, translationsParameters } = useTranslation();
  const { locale } = useTranslation();
  const router = useRouterWithLang();
  const query = router.query;
  const { brand, page: routerPage } = router.query;
  const page = routerPage || 1;
  const category = useSelector((state) => state?.categories).find(
    (category) => category.name === query['category']
  )?.translations[locale];

  const subcategory = useSelector((state) => state?.subcategories).find(
    (subcategory) => subcategory.name === query['subcategory']
  )?.translations[locale];

  const filters = useMemo(() => {
    let queryParams = { ...router.query };
    delete queryParams?.id;
    delete queryParams?.tab;
    delete queryParams?.page;
    delete queryParams?.lang;
    return queryParams;
  }, [router.query]);

  const { title } = useH1Title({ filters });

  const onlyGender = useMemo(
    () =>
      'gender' in filters &&
      !('category' in filters) &&
      !('subcategory' in filters) &&
      !('season' in filters) &&
      !('brand' in filters),
    [filters]
  );
  const onlyBrand = useMemo(
    () =>
      !('gender' in filters) &&
      !('category' in filters) &&
      !('subcategory' in filters) &&
      !('season' in filters) &&
      'brand' in filters,
    [filters]
  );
  const onlySeason = useMemo(
    () =>
      !('gender' in filters) &&
      !('category' in filters) &&
      !('subcategory' in filters) &&
      'season' in filters &&
      !('brand' in filters),
    [filters]
  );
  const hasSubcategory = useMemo(() => 'subcategory' in filters, [filters]);
  const hasCategory = useMemo(() => 'category' in filters, [filters]);

  const generateCanonical = useMemo(() => {
    const regex = /^\/product(?:\?page=\d+)?$/;
    if (regex.test(router.asPath)) {
      return (
        <>
          {Number(page) !== 1 && (
            <link
              rel="prev"
              href={`${process.env.DASHBOARD_URL}${router.asPath.replace(
                /\?page=\d+/,
                ''
              )}?page=${Number(page) - 1}`}
            />
          )}
          <link
            rel="canonical"
            href={`${process.env.DASHBOARD_URL}${router.asPath.replace(
              /\?page=\d+/,
              ''
            )}`}
          />
          <link
            rel="next"
            href={`${process.env.DASHBOARD_URL}${router.asPath.replace(
              /\?page=\d+/,
              ''
            )}?page=${Number(page) + 1}`}
          />
        </>
      );
    } else {
      return (
        <>
          {Number(page) !== 1 && (
            <link
              rel="prev"
              href={`${process.env.DASHBOARD_URL}${router.asPath.replace(
                /(\?|&)page=\d+/,
                ''
              )}&page=${Number(page) - 1}`}
            />
          )}
          <link
            rel="canonical"
            href={`${process.env.DASHBOARD_URL}${router.asPath.replace(
              /(\?|&)page=\d+/,
              ''
            )}`}
          />
          <link
            rel="next"
            href={`${process.env.DASHBOARD_URL}${router.asPath.replace(
              /(\?|&)page=\d+/,
              ''
            )}&page=${Number(page) + 1}`}
          />
        </>
      );
    }
  }, [page, router.asPath]);

  const getTitle = useMemo(() => {
    if (onlySeason) {
      return translationsParameters(
        TRANSLATIONS.products.detail.seo.titleOnlySeason,
        [title]
      ).join('');
    } else if (onlyGender) {
      return translationsParameters(
        TRANSLATIONS.products.detail.seo.titleOnlyGender,
        [title]
      ).join('');
    } else if (onlyBrand) {
      return translationsParameters(
        TRANSLATIONS.products.detail.seo.titleOnlyBrand,
        [title]
      ).join('');
    } else if (hasSubcategory) {
      return translationsParameters(
        TRANSLATIONS.products.detail.seo.titleSubcategory,
        [title]
      ).join('');
    } else if (hasCategory) {
      return translationsParameters(
        TRANSLATIONS.products.detail.seo.titleCategory,
        [title]
      ).join('');
    } else return TRANSLATIONS.products.detail.seo.titleDefault;
  }, [
    TRANSLATIONS.products.detail.seo.titleCategory,
    TRANSLATIONS.products.detail.seo.titleDefault,
    TRANSLATIONS.products.detail.seo.titleOnlyBrand,
    TRANSLATIONS.products.detail.seo.titleOnlyGender,
    TRANSLATIONS.products.detail.seo.titleOnlySeason,
    TRANSLATIONS.products.detail.seo.titleSubcategory,
    hasCategory,
    hasSubcategory,
    onlyBrand,
    onlyGender,
    onlySeason,
    title,
    translationsParameters,
  ]);

  const getDescription = useMemo(() => {
    if (onlySeason) {
      return translationsParameters(
        TRANSLATIONS.products.detail.seo.descriptionOnlySeason,
        [title]
      ).join('');
    } else if (onlyGender) {
      return translationsParameters(
        TRANSLATIONS.products.detail.seo.descriptionOnlyGender,
        [title]
      ).join('');
    } else if (onlyBrand) {
      return translationsParameters(
        TRANSLATIONS.products.detail.seo.descriptionOnlyBrand,
        [title]
      ).join('');
    } else if (hasSubcategory) {
      return translationsParameters(
        TRANSLATIONS.products.detail.seo.descriptionSubcategory,
        [title]
      ).join('');
    } else if (hasCategory) {
      return translationsParameters(
        TRANSLATIONS.products.detail.seo.descriptionCategory,
        [title]
      ).join('');
    } else return TRANSLATIONS.products.detail.seo.titleDefault;
  }, [
    TRANSLATIONS.products.detail.seo.descriptionCategory,
    TRANSLATIONS.products.detail.seo.descriptionOnlyBrand,
    TRANSLATIONS.products.detail.seo.descriptionOnlyGender,
    TRANSLATIONS.products.detail.seo.descriptionOnlySeason,
    TRANSLATIONS.products.detail.seo.descriptionSubcategory,
    TRANSLATIONS.products.detail.seo.titleDefault,
    hasCategory,
    hasSubcategory,
    onlyBrand,
    onlyGender,
    onlySeason,
    title,
    translationsParameters,
  ]);

  return (
    <div>
      <Head>
        <title>{getTitle}</title>
        {generateCanonical}
        <meta property="og:title" content={getTitle} />
        <meta property="og:description" content={getDescription} />
        <meta name={'description'} content={getDescription} />
        <meta
          name="keywords"
          content={`${brand || ''}, ${category || ''}, ${subcategory || ''}`}
        />
        <meta property="og:type" key="og:type" content="website" />
        <meta
          property="og:url"
          key="og:url"
          content={`${process.env.DASHBOARD_URL}/product`}
        />
      </Head>

      {isLoggedIn ? (
        <Dashboard>
          <List />
        </Dashboard>
      ) : (
        <Main>
          <List publicList />
        </Main>
      )}
    </div>
  );
}

export async function getServerSideProps(context) {
  const cookies = context.req.headers.cookie; // Ottiene tutti i cookie dall'header della richiesta

  // Controlla se esistono i cookie 'token' o 'admin_token'
  const token = getCookieFromServer(cookies, 'token');
  const adminToken = getCookieFromServer(cookies, 'admin_token');

  // Se nessuno dei due cookie è presente, reindirizza l'utente a /register
  if (!token && !adminToken) {
    return {
      redirect: {
        destination: '/register',
        permanent: false,
      },
    };
  }

  return {
    props: {}, // Restituisce le props normalmente se i cookie sono presenti
  };
}
